// src/forms/data_hub_actblue_element.js
//
// <data-hub-actblue-donate> — a standalone ActBlue donate module.
//
// Use in pages builder for simple actblue.js customizable button triggers.
//
// Renders the SAME amount-buttons UI the signup form shows after a successful
// submit (create_html_amount_buttons -> actblue_trigger_donation -> ActBlue's
// modal JS API), WITHOUT requiring a <data-hub-signup-form> on the page. On
// connect it reuses the page's DataHub instance or constructs one — exactly the
// pattern <data-hub-signup-form> uses — so dropping this element onto any page
// also bootstraps web tracking and preloads actblue.js.
//
// Token / page-url resolution reuses the existing chains, so on Data Hub-served
// pages (where window.data_hub_organization_metadata is injected) the element
// needs NO ActBlue attributes at all: the org's actblue_default_token drives the
// modal and actblue_default_page_url is the blocked-script fallback link.
//
// Attributes (all optional on Data Hub-served pages):
//   data-hub-organization-id      org uuid — set it when the page has no injected
//                                 org metadata (third-party embeds)
//   data-hub-action-domain        API origin override (parity with signup form)
//   data-hub-actblue-amounts      JSON array of positive integer dollar amounts,
//                                 e.g. "[10,25,50,100]" (invalid -> instance default)
//   data-ab-token                 ActBlue embed token override (else org default)
//   data-hub-actblue-page-url     fallback donate-page URL when actblue.js is
//                                 blocked (else org actblue_default_page_url)
//   data-hub-actblue-refcodes     JSON object of refcodes (parity with signup form)
//   data-hub-title-text           optional title copy (handlebars). Absent = NO
//                                 title — this element never injects default copy
//   data-hub-upsell-text          optional upsell copy (handlebars). Absent = NO
//                                 paragraph — same no-default rule
//   data-hub-show-other-amount    "false" hides the "Other" custom-amount input
//                                 (default: shown)
//   data-hub-web-analytics        'none' disables tracking when THIS element is
//                                 the one that creates the instance
//   data-hub-privacy-policy       parity with signup form (instance creation only)
//   data-hub-css-url              org CSS url layered into the shadow root
//   data-hub-theme / data-hub-*-brand-color   theming (assign_theming_variables)

import { default_theme } from '../helpers/static_vars';
import { create_html_amount_buttons } from './html_element_helpers/form_generators';
import {
    inject_data_hub_css_link,
    inject_html_element_css,
    inject_html_element_utilities_css,
    assign_theming_variables,
} from './html_element_helpers/form_styling';
import { show_error_popover } from './html_element_helpers/error_popover';
import { inject_loading_skeleton } from './html_element_helpers/loading_skeleton';
import { log_console_error_and_track_to_action_endpoint_with_rate_limit } from './html_element_helpers/error_tracking';
import {
    load_actblue_library,
    actblue_is_configured,
    get_cached_actblue_token,
} from '../helpers/actblue_integration';
import { normalize_refcodes_from_object } from '../helpers/refcodes';
import {
    inject_styles,
    parse_b64_or_array_like_data_attribute,
    poll_with_retry,
} from '../helpers';
import { v4 as uuidv4 } from 'uuid';

// How long to wait for an in-flight DataHub instance to finish initializing
// (metadata + user_data fetches) before rendering with whatever resolved.
const INSTANCE_INIT_TIMEOUT_MS = 15000;
const MAX_AMOUNT_BUTTONS = 12;

/**
 * Parses a data-hub-actblue-amounts attribute value into a validated array of
 * positive integer dollar amounts (create_html_amount_buttons requires
 * integers — it falls back to its own defaults for anything else).
 * Pure; exported for tests.
 * @param {*} raw - attribute string like "[10,25,50,100]" (JSON array)
 * @param {Function|null} [error_function]
 * @returns {number[]|null} validated amounts, or null when absent/invalid
 */
export function parse_actblue_amounts_attribute(raw, error_function = null) {
    if (raw == null || (typeof raw === 'string' && raw.trim() === '')) {
        return null;
    }
    let parsed = raw;
    if (typeof parsed === 'string') {
        try {
            parsed = JSON.parse(parsed);
        } catch (e) {
            error_function?.(`data-hub-actblue-amounts is not valid JSON: ${raw}`, null);
            return null;
        }
    }
    if (!Array.isArray(parsed) || parsed.length === 0 || parsed.length > MAX_AMOUNT_BUTTONS) {
        error_function?.(`data-hub-actblue-amounts must be a JSON array of 1-${MAX_AMOUNT_BUTTONS} amounts: ${raw}`, null);
        return null;
    }
    if (!parsed.every((amount) => Number.isInteger(amount) && amount > 0)) {
        error_function?.(`data-hub-actblue-amounts must contain only positive integer dollar amounts: ${raw}`, null);
        return null;
    }
    return parsed;
}

class DataHubActblueDonate extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        // Internal-only rate-limit bucket key (parity with the signup form
        // element). Never logged or sent to the backend.
        this.element_uuid = uuidv4();
    }

    async connectedCallback() {
        if (this.hasAttribute('data-hub-actblue-donate-initialized')) {
            return;
        }
        // Guard synchronously, before any await, so a re-mount can't double-init.
        this.setAttribute('data-hub-actblue-donate-initialized', 'true');

        inject_loading_skeleton(this.shadowRoot);

        await this.initDonateModule().catch((err) => {
            console.error('Error initializing data-hub-actblue-donate:', err);
        });
    }

    async handleError(internalMsg = null, externalMsg = 'Error loading the donation module.') {
        if (externalMsg) {
            show_error_popover(externalMsg, this.shadowRoot);
        }
        log_console_error_and_track_to_action_endpoint_with_rate_limit({
            internal_error_message: internalMsg,
            external_error_message: externalMsg,
            data_hub_instance: this.data_hub_instance,
            metadata: this.data_hub_instance?.page_metadata || {},
            custom_error_data: { source: 'data_hub_actblue_donate_custom_element' },
            rate_limit_bucket_key: this.element_uuid,
            maximum_error_tracking_requests_per_page: 3
        });
    }

    async initDonateModule() {
        // Same reuse-or-create resolution as <data-hub-signup-form>: wait briefly
        // for the bundle globals (registration can upgrade parsed elements a tick
        // before window.DataHub is assigned), then reuse the live instance, wait on
        // an in-flight one, or construct a new one from this element's attributes.
        if (!window.data_hub && !(window.DataHub && window.DataHub.data_hub_class)) {
            try {
                await poll_with_retry(
                    () => !!window.data_hub || !!(window.DataHub && window.DataHub.data_hub_class),
                    { interval: 20, timeout: 3000 }
                );
            } catch (_e) {
                // Fall through — handled below as a visible error.
            }
        }

        if (window.data_hub) {
            this.data_hub_instance = window.data_hub;
        } else if (window.DataHub && window.DataHub.data_hub_class) {
            const is_loading = window.DataHub.hasOwnProperty('data_hub_loading')
                ? window.DataHub.data_hub_loading
                : false;
            if (is_loading) {
                try {
                    await window.DataHub.wait_for_data_hub();
                } catch (err) {
                    this.handleError?.('data_hub load timed out in data-hub-actblue-donate, attempting render anyway: ' + err, null);
                }
                this.data_hub_instance = window.data_hub || null;
            } else {
                window.DataHub.data_hub_loading = true;
                try {
                    await this.initNewDataHub();
                } catch (init_error) {
                    // A synchronous constructor failure must not leave the page's
                    // shared loading gate wedged for later forms or donate elements.
                    window.DataHub.data_hub_loading = false;
                    await this.handleError?.(
                        'Unable to construct DataHub for data-hub-actblue-donate: '
                        + (init_error?.stack || init_error?.message || init_error),
                        null
                    );
                    this.renderInlineFailure('Unable to load the donation module — contact Data Hub administrator.');
                }
                return; // initNewDataHub's callback finishes the render
            }
        }

        if (!this.data_hub_instance) {
            this.handleError?.(
                'window.DataHub not found — bundle did not load or registered under a different global. Cannot initialize donate module.',
                null
            );
            this.renderInlineFailure('Unable to load the donation module — contact Data Hub administrator.');
            return;
        }

        await this.renderWhenInstanceReady();
    }

    async initNewDataHub() {
        const web_analytics_from_attribute =
            this.getAttribute('data-hub-web-analytics')
            || this.dataset.hubWebAnalytics
            || 'all';

        this.data_hub_instance = new window.DataHub.data_hub_class(
            {
                error_function: this.handleError.bind(this),
                actblue_token: this.dataset.abToken || null,
                organization_id: this.getAttribute('data-hub-organization-id') || this.dataset.hubOrganizationId || null,
                data_hub_action_domain: this.getAttribute('data-hub-action-domain') || this.dataset.hubActionDomain || null,
                privacy_policy: this.getAttribute('data-hub-privacy-policy') || this.dataset.hubPrivacyPolicy || null,
                web_settings: web_analytics_from_attribute === 'none' ? { enabled: false } : { enabled: true },
                data_hub_css_url: this.dataset.hubCssUrl || null,
                inject_data_hub_css: false,
            },
            async (instance) => {
                window.data_hub = instance;
                if (window.DataHub && typeof window.DataHub.data_hub_loading !== 'undefined') {
                    window.DataHub.data_hub_loading = false;
                }
                this.data_hub_instance = instance;
                try {
                    await this.renderWhenInstanceReady();
                } catch (render_error) {
                    this.handleError?.(
                        'Error rendering data-hub-actblue-donate after DataHub init: '
                        + (render_error?.stack || render_error?.message || render_error),
                        null
                    );
                }
            }
        );
    }

    async renderWhenInstanceReady() {
        // The buttons capture the ActBlue token and fallback URL at render time,
        // so wait for init (org metadata -> actblue_default_token/_page_url) to
        // finish. A timeout renders anyway — actblue_trigger_donation re-resolves
        // the token at click time, so late metadata still works for the modal.
        if (this.data_hub_instance?.is_initialized === false) {
            try {
                await poll_with_retry(
                    () => this.data_hub_instance?.is_initialized === true,
                    { interval: 50, timeout: INSTANCE_INIT_TIMEOUT_MS }
                );
            } catch (_e) {
                this.handleError?.('data-hub-actblue-donate: DataHub init did not finish in time; rendering with current state.', null);
            }
        }

        // Make sure the actblue.js load settled (shared promise from the
        // constructor's init_actblue; environmental failures warn internally) so
        // actblue_is_configured() below reflects the real outcome, not a race.
        try {
            await load_actblue_library();
        } catch (_e) {
            // Blocked/timed out — render falls back to the direct donate link.
        }

        this.renderDonateModule();
    }

    renderDonateModule() {
        const error_function = this.handleError.bind(this);

        // Element refcodes stay local. The button click merges them with the
        // page-wide constructor/URL refcodes without mutating window.data_hub.
        const element_refcodes = normalize_refcodes_from_object(
            parse_b64_or_array_like_data_attribute(this.dataset.hubRefcodes || this.dataset.hubActblueRefcodes) || {}
        ) || {};

        // Attribute wins; else the instance's configured actblue_amounts (same
        // source the form upsell uses via getDefaultAbAmountsString); else let
        // create_html_amount_buttons apply its own default.
        const instance_amounts = Array.isArray(this.data_hub_instance?.actblue_amounts)
            && this.data_hub_instance.actblue_amounts.every((amount) => Number.isInteger(amount) && amount > 0)
            ? this.data_hub_instance.actblue_amounts
            : null;
        const amounts = parse_actblue_amounts_attribute(this.dataset.hubActblueAmounts, error_function)
            || instance_amounts;
        const actblue_token = this.dataset.abToken || get_cached_actblue_token();
        const actblue_page_url = this.dataset.hubActbluePageUrl
            || this.data_hub_instance?.actblue_default_page_url
            || this.data_hub_instance?.organization_metadata?.actblue_default_page_url
            || null;

        // Blocked actblue.js AND nowhere to link: render nothing rather than an
        // apology (mirrors add_actblue_buttons' skip on the form success path).
        if (!actblue_is_configured() && !actblue_page_url) {
            this.shadowRoot.innerHTML = '';
            return;
        }

        // Theming (data-hub-theme / individual color attrs / org theme_json).
        this.default_theme = default_theme;
        assign_theming_variables(this);

        const content_container = document.createElement('div');
        content_container.classList.add(
            'data_hub_actblue_donate_container',
            'relative', 'data-hub-container', 'container',
            'max-w-full', 'mx-auto', 'mt-0', 'mb-0', 'p-4',
        );

        const buttons_element = create_html_amount_buttons({
            amounts: amounts || undefined,
            refcodes: element_refcodes,
            donor_data: this.data_hub_instance?.user_data || {},
            actblue_token,
            target_container: content_container,
            actblue_page_url,
            // No copy unless authored: '' overrides create_html_amount_buttons'
            // generic title default, so an attribute-less element renders ONLY
            // the logo + buttons. Page copy belongs to the page (e.g. Heading /
            // Text components above this element on hosted pages).
            title_text: this.dataset.hubTitleText ?? '',
            upsell_text: this.dataset.hubUpsellText ?? '',
            show_custom_amount: this.dataset.hubShowOtherAmount !== 'false',
            error_function,
            data_hub_instance: this.data_hub_instance,
        });
        if (!buttons_element) {
            this.handleError?.('create_html_amount_buttons returned nothing in data-hub-actblue-donate', null);
            this.shadowRoot.innerHTML = '';
            return;
        }
        content_container.appendChild(buttons_element);

        this.shadowRoot.innerHTML = '';
        // Same shadow-root CSS stack as the signup form element: org CSS link,
        // element CSS, bundled Tailwind utilities, then theme variables — kept in
        // the shadow root so utility selectors never leak into the host page.
        const data_hub_css_url = this.dataset.hubCssUrl
            || this.data_hub_instance?.data_hub_css_url
            || null;
        inject_data_hub_css_link(this.shadowRoot, {
            data_hub_css_url,
            error_function,
        });
        inject_html_element_css(this.shadowRoot);
        inject_html_element_utilities_css(this.shadowRoot);
        inject_styles(this.theme, this.shadowRoot);
        this.shadowRoot.appendChild(content_container);
    }

    renderInlineFailure(message) {
        const error_container = document.createElement('div');
        error_container.style.cssText = 'padding:24px;text-align:center;font-family:system-ui,sans-serif;color:#6b7280;';
        const message_p = document.createElement('p');
        message_p.style.cssText = 'font-size:14px;';
        message_p.textContent = message;
        error_container.appendChild(message_p);
        this.shadowRoot.innerHTML = '';
        this.shadowRoot.appendChild(error_container);
    }
}

// Registered explicitly by main.js AFTER window.DataHub is assigned — same
// ordering contract (and for the same upgrade-race reason) as
// register_data_hub_signup_form_element.
export function register_data_hub_actblue_donate_element() {
    if (typeof window !== 'undefined'
        && window.customElements
        && !window.customElements.get('data-hub-actblue-donate')) {
        window.customElements.define('data-hub-actblue-donate', DataHubActblueDonate);
        window.data_hub_actblue_donate = DataHubActblueDonate;
    }
}
